import React, { useEffect, useState, useMemo, useCallback } from "react";
import { LuActivity, LuMail } from "react-icons/lu";
import styles from "./TrancheApproval.module.css";
import clsx from "clsx";
import { getTrancheHistory } from "../../service/api";
import Skeleton from "react-loading-skeleton";
import { TrancheLogIcon, TrancheModule } from "../../utils/constant";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { FaCircle, FaBan } from "react-icons/fa";
import { AiOutlineSend } from "react-icons/ai";
import { FiEdit } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { getTrancheLogs } from "../../service/api";
import { TrancheBotV2Status } from "../../utils/constant";
import { getISODate } from "../../utils/dateUtils";

const ICONS = {
  cancelled: (
    <FaBan
      size={18}
      className={styles.redIcon}
      title="Cancelled"
      aria-label="cancelled"
    />
  ),
  failed: (
    <RxCross2
      size={20}
      className={styles.redIcon}
      title="Failed / Rejected"
      aria-label="failed"
    />
  ),
  sent: (
    <AiOutlineSend
      size={20}
      className={styles.infoIcon}
      title="Sent for approval"
      aria-label="sent for approval"
    />
  ),
  success: (
    <IoCheckmarkCircleOutline
      size={24}
      className={styles.successIcon}
      title="Success"
      aria-label="success"
    />
  ),
  proposed: (
    <FiEdit
      size={20}
      className={styles.yellowIcon}
      title="Proposed"
      aria-label="proposed"
    />
  ),
  red: (
    <FaCircle
      size={18}
      className={styles.redIcon}
      title="Error"
      aria-label="error"
    />
  ),
  yellow: (
    <LuMail
      size={20}
      className={styles.yellowIcon}
      title="Info"
      aria-label="info"
    />
  ),
  green: (
    <IoCheckmarkCircleOutline
      size={24}
      className={styles.successIcon}
      title="Success"
      aria-label="success"
    />
  ),
  default: (
    <LuMail
      size={20}
      className={styles.yellowIcon}
      title="Activity"
      aria-label="activity"
    />
  ),
};

const resolveIcon = (status = "", iconType = "") => {
  switch (status) {
    case TrancheBotV2Status.PENDING:
      return ICONS.yellow; // waiting state

    case TrancheBotV2Status.ACTIVE:
      return ICONS.proposed; // editable draft

    case TrancheBotV2Status.SENT_FOR_APPROVAL:
      return ICONS.sent; // sent icon

    case TrancheBotV2Status.SUCCESS:
      return ICONS.success; // green tick

    case TrancheBotV2Status.REJECTED:
    case TrancheBotV2Status.FAILED:
      return ICONS.failed; // red cross

    case TrancheBotV2Status.CANCELLED:
      return ICONS.cancelled; // ban icon

    case TrancheBotV2Status.EXPIRED:
      return ICONS.red; // red dot

    default: {
      // fallback based on TrancheLogIcon color
      switch (iconType) {
        case TrancheLogIcon.GREEN:
          return ICONS.green;
        case TrancheLogIcon.RED:
          return ICONS.red;
        case TrancheLogIcon.YELLOW:
          return ICONS.yellow;
        default:
          return ICONS.default;
      }
    }
  }
};

const ActivityCard = () => {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTrancheLogs = useCallback(async () => {
    try {
      const payload = {
        page: 1,
        limit: 5,
        moduleName: TrancheModule.TRANCHE_BOT,
      };
      const res = await getTrancheHistory(payload);

      setLogs(res?.data?.docs || []);
    } catch (err) {
      console.error("Failed to fetch tranche logs:", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrancheLogs();
  }, [fetchTrancheLogs]);

  const renderedLogs = useMemo(() => {
    if (!logs?.length) {
      return <p className={styles.noActivity}>No recent activity.</p>;
    }

    return logs.map((item) => (
      <li key={item._id} className={styles.activityItem}>
        <span className={styles.activityIcon}>
          {resolveIcon(item.status, item.icon)}
        </span>
        <div className={styles.activityBody}>
          <p className={styles.activityText}>{item.message}</p>
          {item.createdAt && (
            <div className={styles.activityTime}>{getISODate(item.createdAt)}</div>
          )}
        </div>
      </li>
    ));
  }, [logs]);

  return (
    <div className={styles.rightColumn}>
      <div className={clsx(styles.rightCard, loading && styles.loadingWidth, styles.fullHeight)}>
        <h2 className={styles.activityTitle}>
          <LuActivity size={24} /> Recent Activity
        </h2>

        {loading ? (
          <Skeleton count={5} height={50} width="100%" />
        ) : (
          <ul className={styles.activityList}>{renderedLogs}</ul>
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
